import { I18N_DEFAULT_NAMESPACE, I18N_NAMESPACES } from '@shared/constants';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { trpcBackend } from './trpcBackend';

/**
 * i18next configuration with database-backed translations
 *
 * Type Safety:
 * - Translation keys are type-safe via auto-generated types in src/types/i18next.d.ts
 * - To regenerate types after adding translations: pnpm i18n:generate-types
 * - Types are automatically generated from AppText table (locale: en-GB)
 *
 * Namespaces:
 * - All valid namespaces are defined in @shared/constants/i18n.ts
 * - 'translation': Default namespace for common/shared translations
 * - 'risk': Risk management domain-specific translations
 *
 * Usage Pattern (RECOMMENDED):
 * ```typescript
 * // Use namespace parameter for type safety
 * const { t } = useTranslation('risk');
 * t('impacts.help'); //Fully typed with autocomplete
 * ```
 */
i18n.use(trpcBackend)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en-GB',
        lng: 'en-GB',
        ns: [...I18N_NAMESPACES],
        defaultNS: I18N_DEFAULT_NAMESPACE,

        // Skip the language-stripped fallback variant (e.g. `en` for `en-GB`).
        // Both `lng` and `fallbackLng` are `en-GB`, so the extra
        // `en.translation` / `en.risk` `getTranslations` round-trips i18next
        // fires by default (`load: 'all'`) are pure waste — they halve the
        // pageload `appText.getTranslations` batch (4 calls → 2).
        load: 'currentOnly',

        react: {
            useSuspense: true,
        },

        interpolation: {
            escapeValue: false,
        },

        // Type safety options
        returnNull: false, // Return key instead of null for missing translations
        returnEmptyString: false, // Return key instead of empty string

        // debug: import.meta.env.DEV,

        cache: {
            enabled: true,
        },
    });

export default i18n;
